import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { FaFileAlt, FaFileExcel, FaFilePdf, FaFileWord, FaFileImage, FaTrashAlt, FaCloudUploadAlt } from 'react-icons/fa';

const MAX_MB = 20;

const fileIcon = (name = '') => {
  const ext = (name.split('.').pop() || '').toLowerCase();
  if (ext === 'pdf')                            return { Icon: FaFilePdf,   color: '#e53e3e' };
  if (['xls','xlsx','csv'].includes(ext))       return { Icon: FaFileExcel, color: '#38a169' };
  if (['doc','docx'].includes(ext))             return { Icon: FaFileWord,  color: '#3182ce' };
  if (['png','jpg','jpeg','gif','webp'].includes(ext)) return { Icon: FaFileImage, color: '#d69e2e' };
  return { Icon: FaFileAlt, color: '#718096' };
};

const fmt = (bytes) => {
  if (bytes < 1024)       return `${bytes} B`;
  if (bytes < 1024 ** 2)  return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 ** 2).toFixed(2)} MB`;
};

const FileUploader = ({ files, onChange, existingAttachments = [] }) => {
  const inputRef = useRef(null);

  const handleDrop = (e) => {
    e.preventDefault();
    const dropped = Array.from(e.dataTransfer.files || []);
    addFiles(dropped);
  };

  const handlePick = (e) => {
    const picked = Array.from(e.target.files || []);
    addFiles(picked);
    e.target.value = '';
  };

  const addFiles = (newFiles) => {
    const valid = newFiles.filter(f => f.size <= MAX_MB * 1024 * 1024);
    onChange([...files, ...valid]);
  };

  const remove = (idx) => onChange(files.filter((_, i) => i !== idx));

  return (
    <div>
      {/* Drop zone */}
      <div
        onDrop={handleDrop}
        onDragOver={e => e.preventDefault()}
        onClick={() => inputRef.current?.click()}
        className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-py-5 tw-rounded-lg tw-border-2 tw-border-dashed tw-border-slate-300 hover:tw-border-blue-400 hover:tw-bg-blue-50/40 tw-cursor-pointer tw-transition-colors tw-bg-slate-50"
      >
        <FaCloudUploadAlt size={22} className="tw-text-slate-400" />
        <span className="tw-text-xs tw-text-slate-500">
          <span className="tw-text-blue-600 tw-font-semibold">Upload file</span> · Drop files or click here to add files, file size &lt; {MAX_MB}MB
        </span>
        <input ref={inputRef} type="file" multiple className="tw-hidden" onChange={handlePick} />
      </div>

      {/* Staged files */}
      {files.length > 0 && (
        <div className="tw-flex tw-flex-col tw-gap-1.5 tw-mt-2">
          {files.map((f, i) => {
            const { Icon, color } = fileIcon(f.name);
            return (
              <div key={i} className="tw-flex tw-items-center tw-gap-2.5 tw-px-3 tw-py-2 tw-rounded tw-bg-slate-50 tw-border tw-border-slate-200">
                <Icon size={14} style={{ color, flexShrink: 0 }} />
                <span className="tw-flex-1 tw-text-sm tw-text-slate-700 tw-font-medium tw-truncate">{f.name}</span>
                <span className="tw-text-[11px] tw-text-slate-400 tw-flex-shrink-0">{fmt(f.size)}</span>
                <button type="button" onClick={() => remove(i)}
                  className="tw-border-0 tw-bg-transparent tw-text-slate-300 hover:tw-text-red-500 tw-cursor-pointer tw-p-0 tw-flex-shrink-0">
                  <FaTrashAlt size={11} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Existing attachments (edit mode) */}
      {existingAttachments.length > 0 && (
        <div className="tw-flex tw-flex-col tw-gap-1.5 tw-mt-2">
          {existingAttachments.map(att => {
            const { Icon, color } = fileIcon(att.file_name);
            return (
              <div key={att.id} className="tw-flex tw-items-center tw-gap-2.5 tw-px-3 tw-py-2 tw-rounded tw-bg-emerald-50 tw-border tw-border-emerald-200">
                <Icon size={14} style={{ color, flexShrink: 0 }} />
                <a href={att.url} target="_blank" rel="noreferrer"
                  className="tw-flex-1 tw-text-sm tw-text-emerald-700 tw-font-medium tw-truncate hover:tw-underline">
                  {att.file_name}
                </a>
                <span className="tw-text-[11px] tw-text-emerald-500 tw-flex-shrink-0">{fmt(att.byte_size)}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

FileUploader.propTypes = {
  files:               PropTypes.array.isRequired,
  onChange:            PropTypes.func.isRequired,
  existingAttachments: PropTypes.array,
};

export default FileUploader;
